import type { ReactElement } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ChatMessageSummary, ComplianceStatusResponse, CrowdBanSummary, GetFanClubStatusResponse, ReportCategory, ShowCaptionEvent, ShowSummary, ShowViewerChatAccess, ShowViewerRole, StreamSignal } from '@fetmode/types';
import { REPORT_CATEGORIES } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { t } from '@fetmode/i18n';
import { formatUsername, isUuid, normalizeUsernameParam } from '@fetmode/utils';
import { Button, ConfirmModal, ShowChatPanel, ShowChatShell } from '@fetmode/ui';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useAuth, useAuthModal } from '../auth/index.js';
import { useCoinShop } from '../wallet/index.js';
import { resolveApiUrl, useVisitor, useLivePublisher, useLiveSubscriber } from '@fetmode/app-core';
import { useInLiveRoom } from '../../shared/in-live-room-context.js';
import {
  armNotificationPermission,
  notifyStreamWait,
  useLiveStreamWait,
} from '../../shared/live-stream-wait-context.js';
import { camBadgeClass, camBadgeLabel } from '../home/cam-show-badge.js';
import { ModelStorefront } from './ModelStorefront.js';
import { PrivateChatTab } from './PrivateChatTab.js';
import { PrivateShowPanel } from './PrivateShowPanel.js';
import { parseJoinRestriction, type RoomBanOverlayState } from './show-join-restriction.js';
import { hasShowFloorAccess, isConfirmBeforeChargeFloor, isOutsideActiveShow } from './show-join-gate.js';
import { ShowPlayerGate } from './ShowPlayerGate.js';
import { ShowPromoOverlay } from './ShowPromoOverlay.js';
import type { ShowPromoAction } from './show-promo-slides.js';
import { ShowTypeAccessPanel } from './ShowTypePanel.js';
import { TipPanel } from './TipPanel.js';
import { TipMenuBar } from './TipMenuBar.js';
import { GamesMenuBar } from './GamesMenuBar.js';
import { JukeboxMenuBar } from './JukeboxMenuBar.js';
import { ToyControlBar } from './ToyControlBar.js';
import { RoomBarGroup } from './RoomBarGroup.js';
import { ShowEndFeedbackModal } from './ShowEndFeedbackModal.js';
import type { RoomPlayerMenu } from './room-player-menu.js';
import { isChatActiveShow } from './show-room-access.js';
import { useLiveRoomSounds } from './useLiveRoomSounds.js';
import { shouldPollShowForGoLive } from './show-room-poll.js';
import { formatWatchingLabel } from './format-watching-label.js';
import { FlashSpyOverlay } from './FlashSpyOverlay.js';
import { CpmRunwayOverlay } from './CpmRunwayOverlay.js';
import { useShowRealtime } from './useShowRealtime.js';
import { ShowLivePlayer } from './ShowLivePlayer.js';
import { ShowRoomCaptureBar } from './ShowRoomCaptureBar.js';
import camSampleUrl from '../../assets/demo/cam-sample.mp4';
import './ShowRoomPage.css';
const CHAT_POLL_MS = 2_000;
const SHOW_POLL_MS = 10_000;
const GUEST_PRESENCE_HEARTBEAT_MS = 30_000;
const FAN_PRESENCE_HEARTBEAT_MS = 20_000;
const apiBaseUrl = resolveApiUrl(import.meta.env);

function formatTimeRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${String(hours)}h ${String(minutes)}m`;
  }
  if (minutes > 0) {
    return `${String(minutes)}m ${String(seconds)}s`;
  }
  return `${String(seconds)}s`;
}

export function ShowRoomPage(): ReactElement {
  const { username: usernameParam } = useParams<{ username: string }>();
  const username = usernameParam ? normalizeUsernameParam(usernameParam) : '';
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { visitor } = useVisitor();
  const { requireAuth, openAuthModal } = useAuthModal();
  const { openCoinShop, requireCoins, requireSpendCoins, lastWalletTotal } = useCoinShop();
  const { setInLiveRoom } = useInLiveRoom();
  const { wait, notifyArmed, startWait, clearWait } = useLiveStreamWait();
  const [show, setShow] = useState<ShowSummary | null>(null);
  const activeShowId = show?.id ?? null;
  const showStatus = show?.status ?? null;
  const [viewerCount, setViewerCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [privateQueueOpen, setPrivateQueueOpen] = useState(false);
  const [coinBalance, setCoinBalance] = useState(0);
  const [compliance, setCompliance] = useState<ComplianceStatusResponse | null>(null);
  const [tipAmount, setTipAmount] = useState('10');
  const [tipMessage, setTipMessage] = useState('');
  const [tipError, setTipError] = useState<string | null>(null);
  const [tipSuccess, setTipSuccess] = useState(false);
  const [isTipping, setIsTipping] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportCategory, setReportCategory] = useState<ReportCategory>('HARASSMENT');
  const [reportDetails, setReportDetails] = useState('');
  const [reportAlsoBlock, setReportAlsoBlock] = useState(true);
  const [safetyError, setSafetyError] = useState<string | null>(null);
  const [safetySuccess, setSafetySuccess] = useState<string | null>(null);
  const [isSafetySubmitting, setIsSafetySubmitting] = useState(false);
  const [roomBan, setRoomBan] = useState<RoomBanOverlayState | null>(null);
  const [kickedUntil, setKickedUntil] = useState<string | null>(null);
  const [crowdBan, setCrowdBan] = useState<CrowdBanSummary | null>(null);
  const [isPayingUnban, setIsPayingUnban] = useState(false);
  const [voteKickTarget, setVoteKickTarget] = useState<{ userId: string; username: string } | null>(
    null,
  );
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessageSummary[]>([]);
  const [chatText, setChatText] = useState('');
  const [chatError, setChatError] = useState<string | null>(null);
  const [isChatSending, setIsChatSending] = useState(false);
  const [chatTab, setChatTab] = useState<'public' | 'private'>('public');
  const [countdownLabel, setCountdownLabel] = useState<string | null>(null);
  const [viewerRole, setViewerRole] = useState<ShowViewerRole | null>(null);
  const [fanClub, setFanClub] = useState<GetFanClubStatusResponse | null>(null);
  const [viewerChatAccess, setViewerChatAccess] = useState<ShowViewerChatAccess | null>(null);
  const [showStorefrontOnly, setShowStorefrontOnly] = useState(false);
  const chatSinceRef = useRef<string | undefined>(undefined);
  const prevIsViewerRef = useRef<boolean | undefined>(undefined);
  const announcedEntryShowIdRef = useRef<string | null>(null);
  const leftShowRef = useRef<string | null>(null);
  const joinedShowRef = useRef<string | null>(null);
  const joiningShowRef = useRef<string | null>(null);
  const skipAutoJoinRef = useRef(false);
  const streamListenersRef = useRef(new Set<(signal: StreamSignal) => void>());
  const subscribeStreamSignals = useCallback((handler: (signal: StreamSignal) => void) => {
    streamListenersRef.current.add(handler);
    return () => {
      streamListenersRef.current.delete(handler);
    };
  }, []);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [playerEl, setPlayerEl] = useState<HTMLDivElement | null>(null);
  const [openPlayerMenu, setOpenPlayerMenu] = useState<RoomPlayerMenu | null>(null);
  const [tipSlide, setTipSlide] = useState<'tip' | 'tipMenu' | null>(null);
  const [cpmShopOpen, setCpmShopOpen] = useState(false);
  const [playerVideo, setPlayerVideo] = useState<HTMLVideoElement | null>(null);
  const [captureMenuSlot, setCaptureMenuSlot] = useState<HTMLDivElement | null>(null);
  const [c2cOn, setC2cOn] = useState(false);
  const [c2cBusy, setC2cBusy] = useState(false);
  const [c2cStream, setC2cStream] = useState<MediaStream | null>(null);
  const [liveCaption, setLiveCaption] = useState<ShowCaptionEvent | null>(null);
  const { notifyChatMessages, notifySelfJoined, notifySelfTip, resetChatSoundBaseline } =
    useLiveRoomSounds(user?.id);
  const inLiveRoom = isLoading || (show !== null && show.status !== 'ENDED');

  useEffect(() => {
    setLiveCaption(null);
  }, [activeShowId]);

  useEffect(() => {
    if (!liveCaption) {
      return;
    }
    const id = window.setTimeout(() => {
      setLiveCaption(null);
    }, liveCaption.isFinal ? 8_000 : 12_000);
    return () => {
      window.clearTimeout(id);
    };
  }, [liveCaption]);

  useLayoutEffect(() => {
    setInLiveRoom(inLiveRoom);
    return () => {
      setInLiveRoom(false);
    };
  }, [inLiveRoom, setInLiveRoom]);

  const loadShow = useCallback(async () => {
    if (!username) {
      setError(t('common.show.notFound'));
      setShowStorefrontOnly(false);
      return;
    }

    if (isUuid(username)) {
      const legacy = await api.getShow(username);
      navigate(`/@${legacy.show.model.username}`, { replace: true });
      return;
    }

    try {
      const result = await api.getShowByUsername(username);
      setShow(result.show);
      setViewerCount(result.show.viewerCount);
      setViewerRole(result.show.viewerRole ?? null);
      setViewerChatAccess(result.viewerChatAccess ?? null);
      setShowStorefrontOnly(false);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'SHOW_NOT_FOUND') {
        try {
          await api.getModelStorefront(username);
          setShow(null);
          setShowStorefrontOnly(true);
          setError(null);
          return;
        } catch {
          // fall through to not found
        }
      }
      throw err;
    }
  }, [api, navigate, username]);

  const mergeChatMessages = useCallback((incoming: ChatMessageSummary[]) => {
    setChatMessages((current) => {
      if (incoming.length === 0) {
        return current;
      }

      const ids = new Set(current.map((message) => message.id));
      const merged = [...current, ...incoming.filter((message) => !ids.has(message.id))];
      return merged.slice(-100);
    });
  }, []);

  const loadChat = useCallback(
    async (since?: string) => {
      if (!activeShowId) {
        return;
      }

      const result = await api.listShowChat(activeShowId, { since, limit: 50 });
      setCrowdBan(result.crowdBan ?? null);
      if (since) {
        notifyChatMessages(result.messages, false);
        mergeChatMessages(result.messages);
      } else if (result.messages.length > 0) {
        notifyChatMessages(result.messages, true);
        setChatMessages(result.messages);
      }

      const lastMessage = result.messages.at(-1);
      if (lastMessage) {
        chatSinceRef.current = lastMessage.createdAt;
      } else if (!since) {
        chatSinceRef.current = undefined;
      }
    },
    [api, activeShowId, mergeChatMessages, notifyChatMessages],
  );

  const applyJoinFailure = useCallback((err: unknown): void => {
    const parsed = parseJoinRestriction(err);
    if (parsed.kind === 'banned') {
      setRoomBan(parsed.details);
      setKickedUntil(null);
    } else if (parsed.kind === 'kicked') {
      setKickedUntil(parsed.until);
      setRoomBan(null);
    }
    setError(parsed.message);
  }, []);

  const loadWalletAndCompliance = useCallback(async () => {
    const [wallet, complianceStatus] = await Promise.all([
      api.getWallet(),
      api.getComplianceStatus(),
    ]);
    setCoinBalance(wallet.spend.total);
    setCompliance(complianceStatus);
  }, [api]);

  useEffect(() => {
    if (lastWalletTotal !== null) {
      setCoinBalance(lastWalletTotal);
    }
  }, [lastWalletTotal]);

  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') {
        void loadWalletAndCompliance().catch(() => undefined);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadWalletAndCompliance]);

  useEffect(() => {
    let cancelled = false;

    void loadShow()
      .catch(() => {
        if (!cancelled) {
          setError(t('common.show.notFound'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [loadShow]);

  useEffect(() => {
    if (!activeShowId || !show || !isChatActiveShow(show)) {
      setChatMessages([]);
      setCrowdBan(null);
      chatSinceRef.current = undefined;
      announcedEntryShowIdRef.current = null;
      resetChatSoundBaseline();
      return;
    }

    let cancelled = false;

    const bootstrapChat = async (): Promise<void> => {
      if (user && announcedEntryShowIdRef.current !== activeShowId) {
        try {
          const entry = await api.announceShowEntry(activeShowId);
          announcedEntryShowIdRef.current = activeShowId;
          if (!cancelled && entry.message) {
            mergeChatMessages([entry.message]);
          }
        } catch {
          // Retry on next room enter.
        }
      }

      if (cancelled) {
        return;
      }

      await loadChat().catch(() => undefined);

      if (!cancelled && !user) {
        setChatMessages((current) => {
          if (current.length > 0) {
            return current;
          }
          return [
            {
              id: `guest-welcome-${activeShowId}`,
              showId: activeShowId,
              userId: 'guest',
              username: 'guest',
              body: t('common.show.chatWelcomeGuest'),
              kind: 'ENTRANCE',
              createdAt: new Date().toISOString(),
            },
          ];
        });
      }
    };

    void bootstrapChat();

    const intervalId = window.setInterval(() => {
      if (cancelled) {
        return;
      }

      void loadChat(chatSinceRef.current).catch(() => undefined);
    }, CHAT_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeShowId, api, loadChat, mergeChatMessages, resetChatSoundBaseline, show?.id, show?.status, user]);

  const refreshRoomAfterJoin = useCallback(async () => {
    await loadShow();
    chatSinceRef.current = undefined;
    await loadChat().catch(() => undefined);
  }, [loadChat, loadShow]);

  useEffect(() => {
    const isViewer = show?.isViewer ?? false;
    if (prevIsViewerRef.current === false && isViewer && user) {
      notifySelfJoined();
    }
    prevIsViewerRef.current = isViewer;
  }, [notifySelfJoined, show?.isViewer, user]);

  useEffect(() => {
    if (!user) {
      return;
    }

    void loadWalletAndCompliance().catch(() => undefined);
  }, [loadWalletAndCompliance, user]);

  useEffect(() => {
    if (!username || !show) {
      setFanClub(null);
      return;
    }

    if (!show.access.requiresFanClub && show.type !== 'CLUB') {
      setFanClub(null);
      return;
    }

    let cancelled = false;

    void api
      .getFanClubStatus(username)
      .then((status) => {
        if (!cancelled) {
          setFanClub(status);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFanClub(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, show, username, user]);

  useEffect(() => {
    if (!show?.access.scheduledAt || show.status !== 'WAITING') {
      setCountdownLabel(null);
      return;
    }

    const scheduledAt = new Date(show.access.scheduledAt).getTime();
    const updateCountdown = (): void => {
      const remaining = scheduledAt - Date.now();
      if (remaining <= 0) {
        setCountdownLabel(null);
        return;
      }
      setCountdownLabel(formatTimeRemaining(remaining));
    };

    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [show?.access.scheduledAt, show?.status]);

  useEffect(() => {
    // Fallback poll while offline/waiting in case the socket drops; primary updates come via push.
    if (
      !shouldPollShowForGoLive({
        storefrontOnly: showStorefrontOnly,
        status: show?.status ?? null,
      })
    ) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void loadShow().catch(() => undefined);
    }, SHOW_POLL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadShow, show?.status, showStorefrontOnly]);

  const { sendStreamSignal, localPeerId } = useShowRealtime(
    apiBaseUrl,
    { username, showId: activeShowId },
    {
      onShowUpdated: (event) => {
        const eventUsername = event.show.model.username.trim().toLowerCase();
        if (eventUsername !== username.trim().toLowerCase()) {
          return;
        }
        setShow((current) => {
          if (current && current.id !== event.show.id && event.show.status === 'ENDED') {
            return current;
          }
          return {
            ...event.show,
            isViewer: current?.isViewer ?? event.show.isViewer,
            viewerRole: current?.viewerRole ?? event.show.viewerRole,
            viewerSpyExpiresAt: current?.viewerSpyExpiresAt ?? event.show.viewerSpyExpiresAt,
            viewerBilling: event.show.viewerBilling ?? current?.viewerBilling ?? null,
            billingHolds: event.show.billingHolds ?? current?.billingHolds ?? [],
          };
        });
        setViewerCount(event.show.viewerCount);
      },
      onShowEnded: () => {
        void loadShow().catch(() => undefined);
      },
      onStreamSignal: (signal) => {
        for (const listener of streamListenersRef.current) {
          listener(signal);
        }
      },
      onShowCaption: (event) => {
        if (event.showId !== activeShowId) {
          return;
        }
        setLiveCaption(event);
      },
    },
  );

  const liveVideoEnabled =
    show !== null &&
    (show.status === 'LIVE' || show.status === 'PAUSED' || show.status === 'AWAY');
  const {
    remoteStream,
    connecting: liveVideoConnecting,
    health: streamHealth,
  } = useLiveSubscriber({
    enabled: liveVideoEnabled,
    showId: activeShowId,
    localPeerId,
    sendSignal: sendStreamSignal,
    subscribe: subscribeStreamSignals,
  });
  const exclusiveC2cEnabled =
    c2cOn &&
    c2cStream !== null &&
    show?.type === 'EXCLUSIVE' &&
    viewerRole === 'PARTICIPANT' &&
    liveVideoEnabled;
  useLivePublisher({
    enabled: exclusiveC2cEnabled,
    showId: activeShowId,
    localPeerId,
    mediaStream: c2cStream,
    sendSignal: sendStreamSignal,
    subscribe: subscribeStreamSignals,
    channel: 'c2c',
  });
  const modelLiveName = show?.model.displayName?.trim() || formatUsername(username);

  const stopC2cStream = useCallback((stream: MediaStream | null) => {
    if (!stream) {
      return;
    }
    for (const track of stream.getTracks()) {
      track.stop();
    }
  }, []);

  const handleC2cToggle = useCallback(() => {
    if (c2cOn) {
      stopC2cStream(c2cStream);
      setC2cStream(null);
      setC2cOn(false);
      return;
    }
    if (viewerRole !== 'PARTICIPANT' || show?.type !== 'EXCLUSIVE') {
      return;
    }
    requireAuth(() => {
      setC2cBusy(true);
      void navigator.mediaDevices
        .getUserMedia({ video: { width: { ideal: 640 }, height: { ideal: 480 } }, audio: true })
        .then((stream) => {
          setC2cStream(stream);
          setC2cOn(true);
        })
        .catch(() => {
          setError(t('common.show.cam2camDenied'));
        })
        .finally(() => {
          setC2cBusy(false);
        });
    }, 'generic');
  }, [c2cOn, c2cStream, requireAuth, show?.type, stopC2cStream, viewerRole]);

  useEffect(() => {
    if (show?.type !== 'EXCLUSIVE' || viewerRole !== 'PARTICIPANT' || !liveVideoEnabled) {
      if (c2cOn || c2cStream) {
        stopC2cStream(c2cStream);
        setC2cStream(null);
        setC2cOn(false);
      }
    }
  }, [c2cOn, c2cStream, liveVideoEnabled, show?.type, stopC2cStream, viewerRole]);

  useEffect(() => {
    if (!remoteStream || !wait) {
      return;
    }
    if (wait.username.replace(/^@/, '').toLowerCase() !== username.toLowerCase()) {
      return;
    }
    if (wait.notify && document.hidden) {
      notifyStreamWait(wait);
    }
    clearWait();
  }, [remoteStream, wait, username, clearWait]);

  const handleNotifyMe = useCallback(() => {
    if (!activeShowId) {
      return;
    }
    void armNotificationPermission();
    startWait({
      showId: activeShowId,
      username,
      displayName: modelLiveName,
      notify: true,
      autoSwitch: false,
    });
  }, [activeShowId, username, modelLiveName, startWait]);

  const handleKeepBrowsing = useCallback(() => {
    if (!activeShowId) {
      return;
    }
    void armNotificationPermission();
    startWait({
      showId: activeShowId,
      username,
      displayName: modelLiveName,
      notify: true,
      autoSwitch: true,
    });
    navigate('/cams');
  }, [activeShowId, username, modelLiveName, startWait, navigate]);

  useEffect(() => {
    if (!activeShowId || !user || !show) {
      return;
    }

    // Soft-join Main Stage while live, getting ready, or away so tips/chat stay available.
    const canSoftJoin =
      show.status === 'LIVE' || show.status === 'PAUSED' || show.status === 'AWAY';
    if (!canSoftJoin) {
      return;
    }

    const needsFanClub =
      show.access.requiresFanClub && fanClub !== null && !fanClub.isMember;

    if (needsFanClub || show.isViewer) {
      return;
    }

    if (skipAutoJoinRef.current) {
      return;
    }

    // Paid / gated floors go through the join gate so the fan confirms the charge.
    if (show.status === 'LIVE' && isConfirmBeforeChargeFloor(show)) {
      return;
    }

    // On break / away the floor is Main Stage — always free soft-join.
    if (
      (show.status === 'PAUSED' || show.status === 'AWAY') &&
      show.type !== 'MAIN_STAGE'
    ) {
      return;
    }

    // Avoid re-issuing join while a prior attempt for this show is in flight / done.
    if (joinedShowRef.current === activeShowId || joiningShowRef.current === activeShowId) {
      return;
    }

    let cancelled = false;
    joiningShowRef.current = activeShowId;
    setIsJoining(true);

    void api
      .joinShow(activeShowId)
      .then(async (result) => {
        if (cancelled) {
          return;
        }
        joinedShowRef.current = activeShowId;
        setViewerCount(result.viewerCount);
        setViewerRole(result.joinedRole);
        setError(null);
        await refreshRoomAfterJoin();
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          applyJoinFailure(err);
        }
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        if (joiningShowRef.current === activeShowId) {
          joiningShowRef.current = null;
        }
        setIsJoining(false);
      });

    return () => {
      cancelled = true;
      if (joiningShowRef.current === activeShowId) {
        joiningShowRef.current = null;
        setIsJoining(false);
      }
    };
  }, [
    activeShowId,
    api,
    applyJoinFailure,
    fanClub,
    refreshRoomAfterJoin,
    show?.access.entryFeeCoins,
    show?.access.requiresFanClub,
    show?.id,
    show?.isViewer,
    show?.status,
    show?.type,
    user,
  ]);

  // Exclusive never auto-joins. Drop leftover Main Stage joining so Get in line stays clickable.
  useEffect(() => {
    if (show?.type !== 'EXCLUSIVE') {
      return;
    }
    joiningShowRef.current = null;
    setIsJoining(false);
  }, [show?.id, show?.type]);

  useEffect(() => {
    if (!show || !isOutsideActiveShow(show)) {
      return;
    }
    setOpenPlayerMenu((current) =>
      current === 'games' || current === 'toys' || current === 'jukebox' ? null : current,
    );
  }, [show]);

  useEffect(() => {
    if (!activeShowId || !user) {
      return;
    }
    if (!show?.isViewer && !viewerRole) {
      return;
    }

    // Presence TTL is source of truth — do not leave on refresh/pagehide (grace seat).
    const showId = activeShowId;
    let cancelled = false;

    const beat = async (): Promise<void> => {
      try {
        await api.heartbeatShow(showId);
      } catch {
        // Best-effort; scheduler drops stale seats.
      }
    };

    void beat();
    const timer = window.setInterval(() => {
      if (!cancelled) {
        void beat();
      }
    }, FAN_PRESENCE_HEARTBEAT_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeShowId, api, show?.isViewer, user, viewerRole]);

  useEffect(() => {
    if (!activeShowId || user || !showStatus || showStatus === 'ENDED') {
      return;
    }

    const showId = activeShowId;
    const visitorId = visitor.visitorId;
    let cancelled = false;

    const beat = async (): Promise<void> => {
      try {
        const result = await api.touchShowPresence(showId, { visitorId });
        if (!cancelled) {
          setViewerCount(result.viewerCount);
        }
      } catch {
        // Presence is best-effort for guests.
      }
    };

    void beat();
    const timer = window.setInterval(() => {
      void beat();
    }, GUEST_PRESENCE_HEARTBEAT_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      void api.leaveShowPresence(showId, { visitorId }).catch(() => undefined);
    };
  }, [activeShowId, api, showStatus, user, visitor.visitorId]);

  const handlePromoAction = useCallback(
    (action: ShowPromoAction): void => {
      if (!activeShowId || !show) {
        return;
      }

      if (action === 'signup') {
        openAuthModal({ mode: 'register', intent: 'join' });
        return;
      }

      if (action === 'videos') {
        void navigate('/videos');
        return;
      }

      if (action === 'queue') {
        requireAuth(() => {
          setPrivateQueueOpen(true);
        }, 'private');
        return;
      }

      if (action === 'fanClub') {
        requireAuth(() => {
          const runJoinFanClub = (): void => {
            setIsJoining(true);
            setError(null);
            void api
              .joinFanClub(username)
              .then(async (result) => {
                setCoinBalance(result.wallet.total);
                setFanClub((prev) => ({
                  monthlyCoins: result.monthlyCoins,
                  isMember: true,
                  tiers: prev?.tiers ?? [],
                  memberTierId: result.tierId,
                }));
                await refreshRoomAfterJoin();
              })
              .catch((err: unknown) => {
                setError(err instanceof ApiError ? err.message : 'Unable to join fan club.');
              })
              .finally(() => {
                setIsJoining(false);
              });
          };
          void requireSpendCoins(fanClub?.monthlyCoins ?? 0, runJoinFanClub).then((ok) => {
            if (ok) {
              runJoinFanClub();
            }
          });
        }, 'fanClub');
        return;
      }

      const joinBody =
        action === 'spy'
          ? { joinAs: 'SPY' as const, spyMode: 'ONGOING' as const }
          : action === 'flashSpy'
            ? { joinAs: 'SPY' as const, spyMode: 'FLASH' as const }
            : {};

      const joinCost =
        action === 'flashSpy'
          ? (show.access.flashSpyCoins ?? 0)
          : action === 'spy'
            ? 0
            : (show.access.entryFeeCoins ?? 0);

      requireAuth(() => {
        const runJoin = (): void => {
          setIsJoining(true);
          setError(null);
          void api
            .joinShow(activeShowId, joinBody)
            .then(async (result) => {
              setViewerCount(result.viewerCount);
              setViewerRole(result.joinedRole);
              await refreshRoomAfterJoin();
            })
            .catch((err: unknown) => {
              applyJoinFailure(err);
            })
            .finally(() => {
              setIsJoining(false);
            });
        };
        void requireSpendCoins(joinCost, runJoin).then((ok) => {
          if (ok) {
            runJoin();
          }
        });
      }, 'join');
    },
    [
      activeShowId,
      api,
      fanClub?.monthlyCoins,
      navigate,
      openAuthModal,
      applyJoinFailure,
      refreshRoomAfterJoin,
      requireAuth,
      requireSpendCoins,
      show,
      username,
    ],
  );

  const handleFlashSpyJoin = useCallback(
    (mode: 'full' | 'ongoing'): void => {
      if (!activeShowId) {
        return;
      }
      const joinCost =
        mode === 'ongoing' ? 0 : (show?.access.flashSpyCoins ?? show?.access.entryFeeCoins ?? 0);
      const runJoin = (): void => {
        setIsJoining(true);
        setError(null);
        const body =
          mode === 'ongoing'
            ? { joinAs: 'SPY' as const, spyMode: 'ONGOING' as const }
            : {};
        void api
          .joinShow(activeShowId, body)
          .then(async (result) => {
            setViewerCount(result.viewerCount);
            setViewerRole(result.joinedRole);
            await refreshRoomAfterJoin();
          })
          .catch((err: unknown) => {
            applyJoinFailure(err);
          })
          .finally(() => {
            setIsJoining(false);
          });
      };
      void requireSpendCoins(joinCost, runJoin).then((ok) => {
        if (ok) {
          runJoin();
        }
      });
    },
    [activeShowId, api, applyJoinFailure, refreshRoomAfterJoin, requireSpendCoins, show],
  );

  const handleFlashSpyExpired = useCallback((): void => {
    if (!activeShowId) {
      return;
    }
    void api
      .leaveShow(activeShowId)
      .catch(() => undefined)
      .finally(() => {
        setViewerRole(null);
        void loadShow().catch(() => undefined);
      });
  }, [activeShowId, api, loadShow]);

  const handleLeave = (): void => {
    if (activeShowId && user) {
      leftShowRef.current = activeShowId;
      joinedShowRef.current = null;
      joiningShowRef.current = null;
      void api.leaveShow(activeShowId).catch(() => undefined);
    }
    void navigate('/');
  };

  const handleSendTip = async (amountCoins: number, message?: string): Promise<boolean> => {
    if (!user) {
      return false;
    }
    if (
      !requireCoins(coinBalance, amountCoins, () => {
        void handleSendTip(amountCoins, message);
      })
    ) {
      return false;
    }

    setIsTipping(true);
    setTipError(null);
    setTipSuccess(false);

    try {
      const tipNote = (message ?? tipMessage).trim();
      const result = activeShowId
        ? await api.sendTip({
            showId: activeShowId,
            amountCoins,
            message: tipNote.length > 0 ? tipNote : undefined,
          })
        : await api.sendTip({
            modelUsername: username,
            amountCoins,
            message: tipNote.length > 0 ? tipNote : undefined,
          });
      setCoinBalance(result.wallet.total);
      setTipSuccess(true);
      setTipMessage('');
      notifySelfTip();
      if (activeShowId) {
        await loadChat(chatSinceRef.current);
        await loadShow();
      }
      return true;
    } catch (err) {
      setTipError(err instanceof ApiError ? err.message : 'Unable to send tip.');
      return false;
    } finally {
      setIsTipping(false);
    }
  };

  const handleFeedbackSubmit = async (input: {
    rating: number | null;
    comment: string;
    tipCoins: number | null;
  }): Promise<void> => {
    if (!activeShowId) {
      setFeedbackOpen(false);
      return;
    }

    setFeedbackSubmitting(true);
    setFeedbackError(null);

    try {
      if (input.rating !== null) {
        try {
          await api.submitShowFeedback(activeShowId, {
            rating: input.rating,
            comment: input.comment.length > 0 ? input.comment : undefined,
          });
        } catch (err: unknown) {
          if (!(err instanceof ApiError && err.status === 409)) {
            throw err;
          }
        }
      }
      if (input.tipCoins !== null) {
        const sent = await handleSendTip(input.tipCoins);
        if (!sent) {
          setFeedbackError(t('common.show.endShowFeedbackUnable'));
          return;
        }
      }
      setFeedbackOpen(false);
    } catch (err: unknown) {
      setFeedbackError(
        err instanceof ApiError ? err.message : t('common.show.endShowFeedbackUnable'),
      );
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleSendChat = async (): Promise<void> => {
    if (!activeShowId || !user) {
      return;
    }

    const body = chatText.trim();
    if (body.length === 0) {
      return;
    }

    setIsChatSending(true);
    setChatError(null);

    try {
      const result = await api.sendChatMessage(activeShowId, { body });
      setChatText('');
      mergeChatMessages([result.message]);
      chatSinceRef.current = result.message.createdAt;
    } catch (err) {
      setChatError(err instanceof ApiError ? err.message : 'Unable to send message.');
    } finally {
      setIsChatSending(false);
    }
  };

  const handlePayToUnban = async (): Promise<void> => {
    if (!activeShowId) {
      return;
    }
    const unbanPrice = roomBan?.unbanPriceCoins ?? 0;
    if (
      !requireCoins(coinBalance, unbanPrice, () => {
        void handlePayToUnban();
      })
    ) {
      return;
    }
    setIsPayingUnban(true);
    setError(null);
    try {
      await api.payToUnban(activeShowId);
      setRoomBan(null);
      setError(t('common.show.payToUnbanSuccess'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to pay to unban.');
    } finally {
      setIsPayingUnban(false);
    }
  };

  const handleVoteKick = (userId: string): void => {
    const target = chatMessages.find((message) => message.userId === userId);
    setVoteKickTarget({ userId, username: target?.username ?? 'user' });
  };

  const confirmVoteKick = async (): Promise<void> => {
    if (!activeShowId || !voteKickTarget) {
      return;
    }
    const { userId } = voteKickTarget;
    setVoteKickTarget(null);
    try {
      const result = await api.voteCrowdBan(activeShowId, { targetUserId: userId });
      setCrowdBan((current) =>
        current && current.roundId === result.roundId
          ? {
              ...current,
              voteScore: result.voteScore,
              threshold: result.threshold,
              status: result.status === 'GRACE' ? 'GRACE' : 'VOTING',
            }
          : current,
      );
    } catch (err) {
      setChatError(err instanceof ApiError ? err.message : 'Unable to vote.');
    }
  };

  const isWatchOnly = viewerRole === 'SPY' || show?.type === 'PEEP';
  const tipUiVisible = show !== null && show.status !== 'ENDED';
  const canOfflineTip = user !== null && compliance?.canAccess === true;
  const canTip =
    (tipUiVisible && canOfflineTip && !isWatchOnly) ||
    ((showStorefrontOnly || show?.status === 'ENDED') && canOfflineTip);
  const outsideActiveShow = show !== null && isOutsideActiveShow(show);
  const showRoomPlayMenus = tipUiVisible && !outsideActiveShow;
  const canChat =
    tipUiVisible &&
    user !== null &&
    compliance?.canAccess === true &&
    !isWatchOnly &&
    show !== null &&
    hasShowFloorAccess(show) &&
    (viewerChatAccess?.canSend ?? false);

  const chatInputPlaceholder = (() => {
    if (!show || show.status === 'ENDED') {
      return t('common.show.chatEnded');
    }
    if (!user) {
      return t('common.show.chatSignIn');
    }
    if (!compliance?.canAccess) {
      return t('common.show.chatVerify');
    }
    if (isWatchOnly) {
      return show.type === 'PEEP' ? t('common.show.chatPeep') : t('common.show.chatSpy');
    }
    if (!hasShowFloorAccess(show)) {
      return t('common.show.chatJoinFirst');
    }
    if (!viewerChatAccess) {
      return t('common.show.chatPlaceholder');
    }
    if (viewerChatAccess.canSend) {
      if (
        viewerChatAccess.mode === 'MESSAGE_CAP' &&
        viewerChatAccess.messageCap !== null &&
        viewerChatAccess.messagesUsed < viewerChatAccess.messageCap
      ) {
        return t('common.show.chatMessagesRemaining').replace(
          '{{count}}',
          String(viewerChatAccess.messageCap - viewerChatAccess.messagesUsed),
        );
      }
      return t('common.show.chatPlaceholder');
    }
    if (viewerChatAccess.requiresTip) {
      return t('common.show.chatTipFirst');
    }
    if (viewerChatAccess.requiresPurchase) {
      return t('common.show.chatBuyCoins');
    }
    if (viewerChatAccess.mode === 'MESSAGE_CAP') {
      return t('common.show.chatGrayLimit');
    }
    if (viewerChatAccess.mode === 'DISABLED') {
      return t('common.show.chatDisabled');
    }
    return t('common.show.chatDisabled');
  })();

  const handleBlockUser = (): void => {
    if (!show || !user) {
      return;
    }
    setBlockConfirmOpen(true);
  };

  const confirmBlockUser = async (): Promise<void> => {
    if (!show || !user) {
      return;
    }

    setBlockConfirmOpen(false);
    setIsSafetySubmitting(true);
    setSafetyError(null);
    setSafetySuccess(null);

    try {
      await api.blockUser({ blockedUserId: show.model.userId });
      setSafetySuccess(t('common.show.blockSuccess'));
      window.setTimeout(() => {
        void navigate('/');
      }, 800);
    } catch (err) {
      setSafetyError(err instanceof ApiError ? err.message : 'Unable to block user.');
    } finally {
      setIsSafetySubmitting(false);
    }
  };

  const handleSubmitReport = async (): Promise<void> => {
    if (!show || !user || !activeShowId) {
      return;
    }

    setIsSafetySubmitting(true);
    setSafetyError(null);
    setSafetySuccess(null);

    try {
      const result = await api.createReport({
        reportedUserId: show.model.userId,
        category: reportCategory,
        details: reportDetails.trim() || undefined,
        showId: activeShowId,
        alsoBlock: reportAlsoBlock,
      });
      setSafetySuccess(t('common.show.reportSuccess'));
      setShowReportForm(false);
      if (result.blocked) {
        window.setTimeout(() => {
          void navigate('/');
        }, 800);
      }
    } catch (err) {
      setSafetyError(err instanceof ApiError ? err.message : 'Unable to submit report.');
    } finally {
      setIsSafetySubmitting(false);
    }
  };

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.auth.loading')}</p>;
  }

  if (error && !show) {
    return (
      <section>
        <p className="fm-error">{error}</p>
        <Link to="/cams">{t('common.show.backHome')}</Link>
      </section>
    );
  }

  if (!show && showStorefrontOnly && username) {
    return (
      <div className="show-room">
        <ModelStorefront
          username={username}
          presence="offline"
          canTip={canTip}
          coinBalance={coinBalance}
          isTipping={isTipping}
          tipError={tipError}
          onSendTip={handleSendTip}
        />
        <p style={{ margin: '0 var(--show-room-pad) 1rem' }}>
          <Link to="/cams">{t('common.show.backHome')}</Link>
        </p>
      </div>
    );
  }

  if (!show) {
    return (
      <section>
        <p>{t('common.show.notFound')}</p>
        <Link to="/cams">{t('common.show.backHome')}</Link>
      </section>
    );
  }

  if (show.status === 'ENDED') {
    return (
      <div className="show-room">
        <ModelStorefront
          username={show.model.username}
          presence="offline"
          canTip={canTip}
          coinBalance={coinBalance}
          isTipping={isTipping}
          tipError={tipError}
          onSendTip={handleSendTip}
        />
        <p style={{ margin: '0 var(--show-room-pad) 1rem' }}>
          <Link to="/cams">{t('common.show.backHome')}</Link>
        </p>
      </div>
    );
  }

  const showTypeBadge = camBadgeClass(show.type).replace('thumb-card__badge--', '');
  const statusClass = [
    'show-room__status',
    `show-room__status--${showTypeBadge}`,
    show.status === 'LIVE' ? 'show-room__status--live' : null,
    show.status === 'WAITING' ? 'show-room__status--waiting' : null,
    show.status === 'PAUSED' ? 'show-room__status--break' : null,
    show.status === 'AWAY' ? 'show-room__status--away' : null,
  ]
    .filter((value): value is string => value !== null)
    .join(' ');

  const statusLabel =
    show.status === 'AWAY'
      ? t('common.show.away')
      : show.status === 'PAUSED'
        ? t('common.show.onBreak')
        : camBadgeLabel(show.type);

  return (
    <div className="show-room">
        <div className="show-room__layout">
          <div className="show-room__main">
            <header className="show-room__header">
              <div className="show-room__header-main">
                <h1 className="show-room__title">{show.model.displayName}</h1>
              </div>
              <div className="show-room__header-end">
                {user ? (
                  <div className="show-room__safety-actions" aria-label={t('common.show.safetyTitle')}>
                    <button
                      type="button"
                      className={`show-room__safety-btn show-room__safety-btn--overflow${showReportForm ? ' show-room__safety-btn--active' : ''}`}
                      disabled={isSafetySubmitting}
                      onClick={() => {
                        setShowReportForm((current) => !current);
                        setSafetyError(null);
                        setSafetySuccess(null);
                      }}
                    >
                      {t('common.show.report')}
                    </button>
                    <button
                      type="button"
                      className="show-room__safety-btn show-room__safety-btn--overflow"
                      disabled={isSafetySubmitting}
                      onClick={() => {
                        handleBlockUser();
                      }}
                    >
                      {isSafetySubmitting ? t('common.show.blockSending') : t('common.show.block')}
                    </button>
                    <button type="button" className="show-room__safety-btn" onClick={handleLeave}>
                      {t('common.show.leave')}
                    </button>
                  </div>
                ) : null}
                <div className="show-room__header-meta">
                  {countdownLabel ? (
                    <span className="show-room__countdown">
                      {t('common.show.scheduledStartsIn').replace('{{time}}', countdownLabel)}
                    </span>
                  ) : null}
                  <span className={statusClass}>{statusLabel}</span>
                  <span className="show-room__viewers">
                    {formatWatchingLabel(viewerCount, show.spyCount ?? 0)}
                  </span>
                </div>
              </div>
            </header>

            {user && (showReportForm || safetyError || safetySuccess) ? (
              <div className="show-room__safety-panel">
                {safetyError ? <p className="fm-error" style={{ margin: 0 }}>{safetyError}</p> : null}
                {safetySuccess ? (
                  <p style={{ color: 'var(--fm-color-link)', margin: safetyError ? '0.5rem 0 0' : 0 }}>
                    {safetySuccess}
                  </p>
                ) : null}
                {showReportForm ? (
                  <div style={{ marginTop: safetyError || safetySuccess ? '0.75rem' : 0 }}>
                    <h3 className="show-room__safety-panel-title">{t('common.show.reportTitle')}</h3>
                    <label style={{ display: 'block' }}>
                      {t('common.show.reportCategory')}
                      <select
                        className="fm-input"
                        value={reportCategory}
                        onChange={(event) => {
                          setReportCategory(event.target.value as ReportCategory);
                        }}
                      >
                        {REPORT_CATEGORIES.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: 'block', marginTop: '0.75rem' }}>
                      {t('common.show.reportDetails')}
                      <textarea
                        className="fm-input"
                        rows={3}
                        maxLength={1000}
                        value={reportDetails}
                        onChange={(event) => {
                          setReportDetails(event.target.value);
                        }}
                      />
                    </label>
                    <label style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                      <input
                        type="checkbox"
                        checked={reportAlsoBlock}
                        onChange={(event) => {
                          setReportAlsoBlock(event.target.checked);
                        }}
                      />
                      {t('common.show.reportAlsoBlock')}
                    </label>
                    <div style={{ marginTop: '1rem' }}>
                      <Button
                        label={
                          isSafetySubmitting
                            ? t('common.show.reportSending')
                            : t('common.show.reportSubmit')
                        }
                        disabled={isSafetySubmitting}
                        onClick={() => {
                          void handleSubmitReport();
                        }}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {(error && !isJoining) && (
              <div className="show-room__alerts">
                {error && <p className="fm-error" style={{ margin: 0 }}>{error}</p>}
              </div>
            )}

            <div className="show-room__stage">
            <div className="show-room__player">
              <div className="show-room__player-stage" ref={setPlayerEl}>
              <ShowLivePlayer
                status={show.status}
                remoteStream={remoteStream}
                connecting={liveVideoConnecting}
                placeholderSrc={camSampleUrl}
                modelName={modelLiveName}
                notifyArmed={notifyArmed}
                onNotifyMe={handleNotifyMe}
                onKeepBrowsing={handleKeepBrowsing}
                health={streamHealth}
                paid={Boolean(show.viewerBilling) || Boolean(show.viewerSpyExpiresAt)}
                localPreviewStream={c2cStream}
                onVideoElement={setPlayerVideo}
                fullscreenTarget={playerEl}
                caption={liveCaption}
              />
              {show ? (
                <ShowPromoOverlay
                  show={show}
                  user={user}
                  fanClub={fanClub}
                  modelUsername={username}
                  isJoining={isJoining}
                  onAction={handlePromoAction}
                />
              ) : null}
              {show?.viewerSpyExpiresAt && viewerRole === 'SPY' ? (
                <FlashSpyOverlay
                  expiresAt={show.viewerSpyExpiresAt}
                  isSubmitting={isJoining}
                  onJoinFull={() => {
                    handleFlashSpyJoin('full');
                  }}
                  onContinueUnlimited={() => {
                    handleFlashSpyJoin('ongoing');
                  }}
                  onExpired={handleFlashSpyExpired}
                />
              ) : null}
              {show && user ? (
                <CpmRunwayOverlay
                  show={show}
                  userId={user.id}
                  coinBalance={coinBalance}
                  compliance={compliance}
                  shopOpen={cpmShopOpen}
                  onShopOpenChange={setCpmShopOpen}
                  onBalanceChange={setCoinBalance}
                />
              ) : null}
              {kickedUntil ? (
                <div className="show-room-modal show-room-modal--restriction" role="status">
                  <div className="show-room-modal__panel">
                    <h2 className="show-room-modal__title">{t('common.show.kickedFromShow').replace('{{time}}', new Date(kickedUntil).toLocaleTimeString())}</h2>
                  </div>
                </div>
              ) : null}
              {roomBan ? (
                <div className="show-room-modal show-room-modal--restriction" role="dialog" aria-labelledby="room-ban-title">
                  <div className="show-room-modal__panel">
                    <h2 id="room-ban-title" className="show-room-modal__title">
                      {roomBan.permanent
                        ? t('common.show.roomBannedPermanent')
                        : t('common.show.roomBanned')}
                    </h2>
                    {roomBan.cooldownUntil ? (
                      <p className="show-room-modal__muted">
                        {t('common.show.roomBannedCooldown').replace(
                          '{{time}}',
                          new Date(roomBan.cooldownUntil).toLocaleString(),
                        )}
                      </p>
                    ) : null}
                    {roomBan.payToUnbanAllowed && roomBan.unbanPriceCoins ? (
                      <Button
                        label={
                          isPayingUnban
                            ? t('common.show.payToUnbanPaying')
                            : t('common.show.payToUnban').replace(
                                '{{amount}}',
                                String(roomBan.unbanPriceCoins),
                              )
                        }
                        disabled={isPayingUnban}
                        onClick={() => {
                          void handlePayToUnban();
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              ) : null}
              </div>
              {activeShowId ? (
                <ShowPlayerGate
                  show={show}
                  showId={activeShowId}
                  username={username}
                  user={user}
                  api={api}
                  fanClub={fanClub}
                  isJoining={isJoining}
                  compliance={compliance}
                  countdownLabel={countdownLabel}
                  onJoiningChange={setIsJoining}
                  onViewerRoleChange={setViewerRole}
                  onViewerCountChange={setViewerCount}
                  onShowRefresh={refreshRoomAfterJoin}
                  onError={setError}
                  onCoinBalanceChange={setCoinBalance}
                  onFanClubChange={setFanClub}
                  onGetInLine={() => {
                    setPrivateQueueOpen(true);
                  }}
                  onManualJoin={() => {
                    skipAutoJoinRef.current = false;
                  }}
                  onRoomBanned={setRoomBan}
                  onKicked={setKickedUntil}
                />
              ) : null}
            </div>
            {show ? (
              <ShowRoomCaptureBar
                show={show}
                viewerRole={viewerRole}
                remoteStream={remoteStream}
                videoEl={playerVideo}
                c2cOn={c2cOn}
                c2cBusy={c2cBusy}
                onC2cToggle={handleC2cToggle}
                onWallet={(wallet) => {
                  setCoinBalance(wallet.total);
                }}
                requireSpendCoins={requireSpendCoins}
                requireAuth={requireAuth}
                menuSlot={captureMenuSlot}
              />
            ) : null}

            <div className="show-room__actions">
            {activeShowId ? (
              <ShowTypeAccessPanel
                show={show}
                showId={activeShowId}
                username={username}
                user={user}
                api={api}
                viewerRole={viewerRole}
                fanClub={fanClub}
                isJoining={isJoining}
                onJoiningChange={setIsJoining}
                onViewerRoleChange={setViewerRole}
                onViewerCountChange={setViewerCount}
                onShowRefresh={refreshRoomAfterJoin}
                onError={setError}
                onCoinBalanceChange={setCoinBalance}
                onFanClubChange={setFanClub}
                onManualJoin={() => {
                  skipAutoJoinRef.current = false;
                }}
              />
            ) : null}

            {tipUiVisible || activeShowId ? (
              <div className="show-room__tip-controls">
                {activeShowId && show ? (
                  <PrivateShowPanel
                    show={show}
                    showId={activeShowId}
                    user={user}
                    api={api}
                    onError={setError}
                    requestOpen={privateQueueOpen}
                    onRequestOpenChange={setPrivateQueueOpen}
                    onOutcome={(message) => {
                      setError(message);
                    }}
                    onBalanceRefresh={async () => {
                      const wallet = await api.getSpendWallet();
                      setCoinBalance(wallet.total);
                    }}
                    onShowRefresh={refreshRoomAfterJoin}
                    onSessionEnded={({ returnedToPublic }) => {
                      joinedShowRef.current = null;
                      skipAutoJoinRef.current = !returnedToPublic;
                      setViewerRole(null);
                    }}
                    onEndedForFeedback={() => {
                      setFeedbackError(null);
                      setFeedbackOpen(true);
                    }}
                  />
                ) : null}

                {tipUiVisible ? (
                  <>
                    <RoomBarGroup label={t('common.show.tipGroupLabel')} variant="tips">
                      <TipPanel
                      user={user}
                      compliance={compliance}
                      canTip={canTip}
                      coinBalance={coinBalance}
                      tipAmount={tipAmount}
                      onTipAmountChange={setTipAmount}
                      tipMessage={tipMessage}
                      onTipMessageChange={setTipMessage}
                      isTipping={isTipping}
                      tipError={tipError}
                      tipSuccess={tipSuccess}
                      onSendTip={handleSendTip}
                      expanded={tipSlide === 'tip'}
                      onExpandedChange={(open) => {
                        setTipSlide(open ? 'tip' : null);
                      }}
                    />
                    <TipMenuBar
                      showId={activeShowId}
                      modelUsername={username}
                      canTip={canTip}
                      coinBalance={coinBalance}
                      isTipping={isTipping}
                      onBalanceChange={setCoinBalance}
                      playerEl={playerEl}
                      menuOpen={openPlayerMenu === 'tip'}
                      onMenuOpenChange={(open) => {
                        setOpenPlayerMenu(open ? 'tip' : null);
                      }}
                      expanded={tipSlide === 'tipMenu'}
                      onExpandedChange={(open) => {
                        setTipSlide(open ? 'tipMenu' : null);
                      }}
                    />
                    </RoomBarGroup>
                    <RoomBarGroup label={t('common.show.moreActions')} variant="more">
                    <div ref={setCaptureMenuSlot} className="show-room__more-capture" />
                    {showRoomPlayMenus ? (
                      <>
                    <GamesMenuBar
                      showId={activeShowId}
                      modelUsername={username}
                      canTip={canTip}
                      coinBalance={coinBalance}
                      isTipping={isTipping}
                      onSendTip={handleSendTip}
                      onGamePlayed={(wallet) => {
                        setCoinBalance(wallet.total);
                      }}
                      onRefreshChat={() => {
                        void loadChat(chatSinceRef.current);
                      }}
                      playerEl={playerEl}
                      menuOpen={openPlayerMenu === 'games'}
                      onMenuOpenChange={(open) => {
                        setOpenPlayerMenu(open ? 'games' : null);
                      }}
                    />
                    <ToyControlBar
                      modelUsername={username}
                      canTip={canTip}
                      coinBalance={coinBalance}
                      isTipping={isTipping}
                      onSendTip={handleSendTip}
                      playerEl={playerEl}
                      menuOpen={openPlayerMenu === 'toys'}
                      onMenuOpenChange={(open) => {
                        setOpenPlayerMenu(open ? 'toys' : null);
                      }}
                    />
                    <JukeboxMenuBar
                      showId={activeShowId}
                      modelUsername={username}
                      canTip={canTip}
                      coinBalance={coinBalance}
                      isTipping={isTipping}
                      viewerUsername={user?.username ?? null}
                      onBalanceChange={setCoinBalance}
                      playerEl={playerEl}
                      menuOpen={openPlayerMenu === 'jukebox'}
                      onMenuOpenChange={(open) => {
                        setOpenPlayerMenu(open ? 'jukebox' : null);
                      }}
                    />
                      </>
                    ) : null}
                    <button
                      type="button"
                      className="show-room__get-coins"
                      onClick={() => {
                        openCoinShop();
                      }}
                    >
                      {t('common.show.getMoreCoins')}
                    </button>
                    {user ? (
                      <div className="show-room__more-safety">
                        <button
                          type="button"
                          className={`show-room__more-safety-btn${showReportForm ? ' show-room__more-safety-btn--active' : ''}`}
                          disabled={isSafetySubmitting}
                          onClick={() => {
                            setShowReportForm((current) => !current);
                            setSafetyError(null);
                            setSafetySuccess(null);
                          }}
                        >
                          {t('common.show.report')}
                        </button>
                        <button
                          type="button"
                          className="show-room__more-safety-btn"
                          disabled={isSafetySubmitting}
                          onClick={() => {
                            handleBlockUser();
                          }}
                        >
                          {isSafetySubmitting ? t('common.show.blockSending') : t('common.show.block')}
                        </button>
                      </div>
                    ) : null}
                    </RoomBarGroup>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
          </div>
          </div>

          <aside className="show-room__sidebar">
            <ShowChatShell
              className="show-room__chat"
              tablistLabel={t('common.show.chatTitle')}
              tabs={[
                { id: 'public', label: t('common.show.chatTabPublic') },
                { id: 'private', label: t('common.show.chatTabPrivate') },
              ]}
              activeTabId={chatTab}
              onTabChange={(tabId) => {
                setChatTab(tabId === 'private' ? 'private' : 'public');
              }}
            >
              {chatTab === 'public' ? (
                <ShowChatPanel
                  layout="room"
                  messages={chatMessages}
                  messageText={chatText}
                  onMessageTextChange={setChatText}
                  onSend={() => {
                    void handleSendChat();
                  }}
                  isSending={isChatSending}
                  sendError={chatError}
                  canSend={canChat}
                  inputPlaceholder={chatInputPlaceholder}
                  sendLabel={t('common.show.chatSend')}
                  sendingLabel={t('common.show.chatSending')}
                  currentUserId={user?.id}
                  voteKickLabel={t('common.show.voteKick')}
                  onVoteKick={
                    user && (show?.isViewer || viewerRole)
                      ? (userId) => {
                          handleVoteKick(userId);
                        }
                      : undefined
                  }
                  footer={
                    crowdBan ? (
                      <p className="show-room__crowd-ban">
                        {t('common.show.crowdBanPrompt')
                          .replace('{{username}}', crowdBan.targetUsername)
                          .replace('{{score}}', String(crowdBan.voteScore))
                          .replace('{{threshold}}', String(crowdBan.threshold))}
                      </p>
                    ) : null
                  }
                  formatTipLine={({ username: tipUser, amountLabel, message }) => {
                    const base = t('common.show.chatTip')
                      .replace('{{username}}', formatUsername(tipUser))
                      .replace('{{amount}}', amountLabel);
                    return message ? `${base} — ${message}` : base;
                  }}
                />
              ) : (
                <PrivateChatTab
                  username={username}
                  api={api}
                  userId={user?.id ?? null}
                  onWalletChange={setCoinBalance}
                />
              )}
            </ShowChatShell>
          </aside>
        </div>
        <ModelStorefront
          username={show.model.username}
          presence={
            show.status === 'LIVE' ||
            show.status === 'WAITING' ||
            show.status === 'PAUSED' ||
            show.status === 'AWAY'
              ? show.status
              : 'offline'
          }
          canTip={canTip}
          coinBalance={coinBalance}
          isTipping={isTipping}
          onSendTip={handleSendTip}
        />
        <ShowEndFeedbackModal
          isOpen={feedbackOpen}
          onClose={() => {
            if (!feedbackSubmitting) {
              setFeedbackOpen(false);
            }
          }}
          canTip={canOfflineTip}
          coinBalance={coinBalance}
          isSubmitting={feedbackSubmitting || isTipping}
          error={feedbackError}
          onSubmit={handleFeedbackSubmit}
        />
        <ConfirmModal
          open={voteKickTarget !== null}
          title={t('common.dialog.title')}
          message={t('common.show.voteKickConfirm').replace(
            '{{username}}',
            voteKickTarget?.username ?? 'user',
          )}
          confirmLabel={t('common.show.voteKick')}
          cancelLabel={t('common.dialog.cancel')}
          confirmVariant="request"
          onCancel={() => {
            setVoteKickTarget(null);
          }}
          onConfirm={() => {
            void confirmVoteKick();
          }}
        />
        <ConfirmModal
          open={blockConfirmOpen}
          title={t('common.dialog.title')}
          message={t('common.show.blockConfirm')}
          confirmLabel={
            isSafetySubmitting ? t('common.show.blockSending') : t('common.show.block')
          }
          cancelLabel={t('common.dialog.cancel')}
          confirmVariant="request"
          busy={isSafetySubmitting}
          onCancel={() => {
            if (!isSafetySubmitting) {
              setBlockConfirmOpen(false);
            }
          }}
          onConfirm={() => {
            void confirmBlockUser();
          }}
        />
    </div>
  );
}
